import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import GlobalSearch from './GlobalSearch';

// Who may RAISE a Payment Request. MUST stay identical to the backend RAISE gate
// in src/modules/requests/requests.routes.js (authorize(...)). Anyone in this list
// sees the 💳 Payment Requests nav item and can open /requests; approvers among
// them (finance_approver, management, admin) also get the Approval Queue tab.
// One source of truth — imported by both Navigation.js and Sidebar.js.
export const PAYMENT_RAISE_ROLES = ['admin', 'management', 'accounts', 'finance_approver',
  'stores_incharge', 'procurement_officer', 'production_incharge', 'ppc_manager', 'maintenance_incharge', 'merchandiser',
  'deroi_operator'];

// Single source of truth for the top navigation across every page.
// `roles` (optional) restricts an item to those roles.
export const NAV_ITEMS = [
  { label: 'Dashboard', path: '/dashboard' },
  { label: 'Huddle', path: '/huddle' },
  { label: 'Help Slip', path: '/help-slip' },
  { label: 'Merchandising', path: '/merchandising' },
  { label: 'Fabric Calculator', path: '/fabric-calculator', roles: ['admin', 'management', 'procurement_officer', 'merchandiser'] },
  { label: 'Order Execution', path: '/order-execution' },
  { label: 'Deroi Mart Pvt Ltd', path: '/deroi', roles: ['admin', 'management', 'deroi_operator'] },
  { label: 'Procurement', path: '/procurement' },
  { label: 'Vendors', path: '/vendor-review' },
  { label: 'Job Work', path: '/job-work' },
  { label: 'Production', path: '/production' },
  { label: 'Sampling & T&M', path: '/sampling-tms', roles: ['admin', 'management', 'merchandiser', 'ppc_manager', 'production_incharge', 'production_supervisor'] },
  // T&A top-level item for the production team (production_incharge/supervisor/ppc_manager),
  // READ-ONLY (see TNA.js tnaReadOnly) — the cost-free ex-factory schedule they need for deadlines.
  // Other roles reach T&A via the Merchandising / PPC tabs.
  { label: 'T&A', path: '/tna', roles: ['production_incharge', 'production_supervisor', 'ppc_manager'] },
  // Sales Plan (SCHEDULE ONLY — pricing stripped server-side) for the production team's deadlines.
  { label: 'Sales Plan', path: '/sales-plan', roles: ['production_incharge', 'production_supervisor', 'ppc_manager'] },
  { label: 'Quality', path: '/quality' },
  // NOTE: NAV_ITEMS is DEAD — the <Navigation> component that reads it is imported by ~18 pages
  //       and rendered by none (Layout.js renders <Sidebar>), so it is tree-shaken out of the
  //       bundle. Kept in step with Sidebar.js for consistency; this line has NO runtime effect.
  { label: 'Maintenance', path: '/maintenance', roles: ['admin', 'management', 'production_incharge', 'production_supervisor', 'ppc_manager', 'maintenance_incharge'] },
  // hr is here ONLY to verify Day Book entries (mig 176) — Finance.js gives it the Day Book
  // tab and nothing else, and the backend read gate is scoped to match.
  { label: 'Finance', path: '/finance', roles: ['admin', 'management', 'stores_incharge', 'accounts', 'hr', 'deroi_operator'] },
  { label: '🧾 GST Tagging', path: '/gst-tagging', roles: ['admin', 'accounts', 'management'] },
  { label: '💳 Payment Requests', path: '/requests', roles: PAYMENT_RAISE_ROLES },
  { label: 'Performance', path: '/performance', roles: ['admin', 'management', 'hr'] },
  { label: 'HR', path: '/hr', roles: ['admin', 'management', 'hr'] },
  { label: '📍 Field Punch', path: '/field-attendance' },
  { label: '📥 Store Inward', path: '/store-inward', roles: ['admin', 'management', 'stores_incharge', 'store', 'gate'] },
  // NO `roles` KEY, AND /runner IS IN EVERY ROLE_NAV ALLOW-LIST. Anyone in the office may
  // ask for something to be fetched — that is the whole point of pooling, and the request
  // most likely to be lost is the one from somebody with a narrow menu. Curating the AREA
  // list is gated instead, in the API, where a gate belongs.
  { label: '🛵 Runner', path: '/runner' },
  { label: '🚧 Gate Register', path: '/gate-register', roles: ['admin', 'management', 'stores_incharge', 'store', 'gate', 'deroi_operator'] },
  { label: 'My HR', path: '/my-hr' },
  { label: 'Training Assessment', path: '/assessment' },
  { label: 'Training Videos', path: '/training' },
  { label: 'Tasks', path: '/tasks' },
  // Activity log: every role may open it. admin/management see everyone, everyone else sees
  // only themselves — decided SERVER-side, so no roles array here.
  { label: '📋 Activity Log', path: '/activity' },
  { label: 'Masters', path: '/masters' },
  // Surfaced only for users who actually own a risk sign-off (see below). The
  // sentinel role keeps it out of the default/role allow-lists until injected.
  { label: 'Risk Sign-offs', path: '/risk-sign-offs', roles: ['__signoff_owner__'] },
  // Risk Items dashboard — shown to management always, and to owners with open
  // risks. Sentinel role keeps it out of default lists until injected below.
  { label: 'Risk Items', path: '/risk-items', roles: ['__risk_items__'] },
  // Standalone read-only Risk Analysis — surfaced for dept roles (RISK_DEPT_ROLES)
  // who view/sign off RA but have no Order Execution access. Sentinel role until injected.
  { label: 'Risk Analysis', path: '/risk-analysis', roles: ['__ra_view__'] },
  // Standalone Full Kitting — surfaced for the Production Incharge (who has no
  // Order Execution access); OE users reach it via the OE → Full Kitting tab.
  { label: 'Full Kitting', path: '/full-kitting', roles: ['production_incharge'] },
  { label: 'PPM', path: '/ppm', roles: ['production_incharge'] },
];

// Department-scoped roles see ONLY the paths listed here, in NAV_ITEMS order.
// (PPC and Bulletin live as tabs inside the Production page, so '/production'
// covers Production + PPC + Bulletin.) Any role not listed here uses the
// default rule: every item except those whose `roles` exclude it.
export const ROLE_NAV = {
  // /tasks — same finding as accounts: 3 open tasks assigned, screen unreachable.
  // /finance reaches ONE tab for this role — Purchase Invoices, i.e. what Deroi owes.
  // The narrowing lives in Finance.js, the same way `hr` gets only the Day Book.
  deroi_operator: ['/dashboard', '/risk-analysis', '/deroi', '/job-work', '/requests', '/tasks', '/my-hr', '/activity', '/runner', '/finance', '/gate-register'],
  production_incharge: ['/dashboard', '/risk-analysis', '/production', '/sampling-tms', '/tna', '/sales-plan', '/quality', '/full-kitting', '/ppm', '/maintenance', '/requests', '/my-hr', '/activity', '/runner'],
  // /job-work added (mig 189): the job-work quality verdict is theirs to sign.
  // Without this the endpoint exists but the only roles authorised to call it
  // cannot open the page — every JWO would strand at 'delivered'.
  quality_incharge: ['/dashboard', '/risk-analysis', '/quality', '/job-work', '/my-hr', '/activity', '/runner'],
  qc_inspector: ['/dashboard', '/risk-analysis', '/quality', '/job-work', '/my-hr', '/activity', '/runner'],
  // /finance is Day-Book-verification only (mig 176) — the tab list and the backend read gate
  // both clamp hr to the register + totals; it cannot create, edit, delete or reconcile.
  hr: ['/dashboard', '/risk-analysis', '/performance', '/tasks', '/hr', '/my-hr', '/finance', '/activity', '/runner'],
  staff: ['/dashboard', '/my-hr', '/field-attendance', '/activity', '/runner'],
  // Procurement dept: scoped to its own work — Procurement (PO list/creation + the 📦 GRN
  // button lives here), Vendors, and Full Kitting (view). Deliberately NO OE/estimates/GPO,
  // Job Work, Finance, HR, admin. Backend already authorises PO/GRN/vendor endpoints for
  // procurement_officer; this gives the matching nav (and hard-blocks the rest).
  procurement_officer: ['/procurement', '/vendor-review', '/full-kitting', '/requests', '/my-hr', '/activity', '/runner'],
  // /order-execution gives accounts a READ-ONLY GPO view (so the GPO risk banner is reachable);
  // OrderExecution.js restricts them to the GPO tab with no create/edit affordances.
  // /tasks added 2026-08-22: accounts held 4 pending tasks — 2 critical, 3 already past
  // deadline — and could not open the screen they live on. Ten of the fifteen task routes carry
  // `authenticate` and no authorize(), so the server has always permitted this role to list,
  // progress, comment on and COMPLETE a task; only the nav prevented it. ROLE_NAV also drives
  // pathAllowedForRole, so the page was unreachable by URL too, not merely hidden.
  accounts: ['/dashboard', '/finance', '/gst-tagging', '/order-execution', '/risk-analysis', '/requests', '/tasks', '/my-hr', '/activity', '/runner'],
  // Auditor is clamped to HR ONLY (Girija is the sole auditor on prod). HR + the
  // universal My HR / Training Assessment; deliberately NO /dashboard.
  auditor: ['/hr', '/my-hr', '/activity', '/runner'],
  // Store dept: Full Kitting only (the GPO/order they kit against shows inside the
  // Full Kitting screen) + My HR. Deliberately NO /dashboard or any other module —
  // pathAllowedForRole hard-blocks every path not listed here.
  // /job-work is RECEIVE-ONLY for the store: the page shows Raise GRN and nothing else.
  // Creating, editing, batching, invoicing and challans stay on the manage capability,
  // and the server agrees (JWO_RECEIVE guards POST /:id/grn alone).
  // /tasks — same finding as accounts: 2 open tasks assigned, screen unreachable.
  store: ['/full-kitting', '/store-inward', '/job-work', '/gate-register', '/tasks', '/my-hr', '/activity', '/runner'],
  // Gate security: the store module (where the gate-pass exit queue renders) plus the gate
  // register itself — the transcription of his own book, which he of all people should be
  // able to read back. Still no other module.
  gate: ['/store-inward', '/gate-register', '/activity', '/runner'],
  // Maintenance dept. The role has existed in the user_role enum since mig 102 and has been
  // selectable in the admin UI, but was never listed here — and an UNLISTED role is treated as
  // UNRESTRICTED (App.js `if (!allow) return true`). So Ravi Kumar saw Procurement,
  // Merchandising, Production and every other module carrying no roles array, while Maintenance
  // — the one module he is actually for — carried one that omitted him.
  //
  // Scoped to what the BACKEND actually authorises, not more:
  //   /maintenance — DT_ENTRY (maintenance.routes.js:8) covers jobs create/edit/close,
  //                  take-down, schedule-down and downtime open/close; the rest of the module
  //                  is authenticate-only reads. The Downtime tab lives here, so his downtime
  //                  capability is fully reachable from this one path.
  //   /requests    — he IS in PAYMENT_RAISE_ROLES (requests.routes.js:24), so raising a payment
  //                  request is a real capability, not an accident of being unlisted.
  //   /dashboard, /my-hr — the baseline every scoped role gets. /my-hr must be listed
  //                  explicitly: pathAllowedForRole's universal exemptions are only
  //                  /risk-sign-offs, /risk-items, /risk-analysis and /assessment.
  //
  // Deliberately NOT /ppc: he holds DT_ENTRY on two PPC downtime endpoints
  // (planning.routes.js:260,276), but granting the whole PPC module — cutting, stitching,
  // finishing, loading plan, manhour — to reach two endpoints would exceed the capability
  // rather than match it. Raised for a ruling instead.
  maintenance_incharge: ['/dashboard', '/maintenance', '/requests', '/my-hr', '/activity', '/runner'],
};

// Where a role lands after login / when bounced off a disallowed path. Auditor is
// HR-only, so it lands in HR instead of the cross-module dashboard.
export const homeFor = (role) => (role === 'auditor' ? '/hr' : role === 'gate' ? '/store-inward' : role === 'store' ? '/full-kitting' : role === 'procurement_officer' ? '/procurement' : role === 'maintenance_incharge' ? '/maintenance' : '/dashboard');

// Department-head roles in the collaborative Risk Analysis 2.0 (sign-off owners): they
// always see the Risk Sign-offs + Risk Items screens so the workflow is discoverable,
// even before a specific risk is assigned to them. (Every role still gets these injected
// on ownership too.) Mapping: Accounts=commercial, procurement/stores=fabric/trims sourcing,
// ppc_manager=PPC + IE, production_incharge/supervisor=production capacity,
// quality_incharge/qc_inspector=quality/technical, hr=HR.
export const RISK_DEPT_ROLES = [
  'accounts', 'procurement_officer', 'stores_incharge', 'ppc_manager',
  'production_incharge', 'production_supervisor', 'quality_incharge', 'qc_inspector', 'hr',
  'deroi_operator',   // fabric team — RA view (via /order-execution) + sign-off/items
];

export default function Navigation() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user } = useAuth();
  // Count of samples awaiting Merchandiser review — shown as a badge on the
  // Merchandising nav item. Polled lightly so it stays roughly current.
  const [reviewCount, setReviewCount] = useState(0);
  // Count of risk sign-offs awaiting this user — shows the Risk Sign-offs nav
  // item + a badge only when they actually own a pending risk.
  const [signoffPending, setSignoffPending] = useState(0);
  // Open risk items (Build 2): owned count drives the owner badge; total drives
  // the management badge.
  const [riskOpen, setRiskOpen] = useState({ open_owned: 0, open_total: 0 });
  const approver = ['admin', 'management'].includes(user?.role);
  useEffect(() => {
    let alive = true;
    const fetchCount = () => api.get('/sample-bulletins/awaiting-review-count')
      .then(r => { if (alive) setReviewCount(r.data?.data?.count || 0); })
      .catch(() => {});
    const fetchSignoff = () => api.get('/risk/sign-offs/count')
      .then(r => { if (alive) setSignoffPending(r.data?.data?.pending || 0); })
      .catch(() => {});
    const fetchRiskOpen = () => api.get('/risk/items/count')
      .then(r => { if (alive) setRiskOpen(r.data?.data || { open_owned: 0, open_total: 0 }); })
      .catch(() => {});
    fetchCount(); fetchSignoff(); fetchRiskOpen();
    const id = setInterval(() => { fetchCount(); fetchSignoff(); fetchRiskOpen(); }, 60000);
    const onSignoff = () => { fetchSignoff(); fetchRiskOpen(); };
    window.addEventListener('risk:signoff_request', onSignoff);
    return () => { alive = false; clearInterval(id); window.removeEventListener('risk:signoff_request', onSignoff); };
  }, []);
  const btn = (active, color = '#3B3A8F') => ({
    padding: '6px 12px', border: 'none', cursor: 'pointer', borderRadius: 6, fontSize: 13,
    background: active ? '#EEEDF8' : 'transparent',
    color: active ? color : '#666', fontWeight: active ? 600 : 400, whiteSpace: 'nowrap',
  });
  // Department-scoped roles get an explicit allow-list (authoritative); everyone
  // else sees every item except those whose `roles` exclude them.
  const allow = ROLE_NAV[user?.role];
  const base = allow
    ? NAV_ITEMS.filter(i => allow.includes(i.path))
    : NAV_ITEMS.filter(i => !i.roles || i.roles.includes(user?.role));
  // My HR self-service is UNIVERSAL — every logged-in employee gets it,
  // regardless of role: anyone can be named a reliever and must reach My HR to
  // accept/decline, and everyone needs their own attendance/leave/payslips.
  const paths = new Set(base.map(i => i.path));
  paths.add('/my-hr');
  // Training Assessment is UNIVERSAL — every logged-in role takes their own test.
  paths.add('/assessment');
  // Training Videos is UNIVERSAL — every logged-in role may watch them (mig 291).
  paths.add('/training');
  // Huddle is UNIVERSAL except the gate — everyone attends the 10:05 stand-up and fills in
  // their own sheet. Gate Security does not attend (ruling 9).
  if (user?.role !== 'gate') paths.add('/huddle');
  // Help Slip is UNIVERSAL with no exclusions — gate included.
  paths.add('/help-slip');
  // Risk screens: always visible to department-head roles (collaborative RA sign-off),
  // and to anyone with a pending sign-off / open risk they own (ownership injection).
  const riskDept = RISK_DEPT_ROLES.includes(user?.role);
  if (signoffPending > 0 || riskDept) paths.add('/risk-sign-offs');
  // Risk Items: management always (oversight); department heads; owners with open risks.
  const riskItemsBadge = approver ? riskOpen.open_total : riskOpen.open_owned;
  if (approver || riskDept || riskOpen.open_owned > 0) paths.add('/risk-items');
  if (riskDept) paths.add('/risk-analysis');
  const items = NAV_ITEMS.filter(i => paths.has(i.path));
  return (
    <nav style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
      {items.map(item => (
        <button key={item.path} onClick={() => navigate(item.path)} style={btn(pathname === item.path)}>
          {item.label}
          {item.path === '/merchandising' && reviewCount > 0 && (
            <span title={`${reviewCount} sample(s) awaiting review`}
              style={{ marginLeft: 6, background: '#F59E0B', color: '#fff', borderRadius: 10, padding: '0 7px', fontSize: 11, fontWeight: 700 }}>{reviewCount}</span>
          )}
          {item.path === '/risk-sign-offs' && signoffPending > 0 && (
            <span title={`${signoffPending} risk(s) awaiting your sign-off`}
              style={{ marginLeft: 6, background: '#E22D2D', color: '#fff', borderRadius: 10, padding: '0 7px', fontSize: 11, fontWeight: 700 }}>{signoffPending}</span>
          )}
          {item.path === '/risk-items' && riskItemsBadge > 0 && (
            <span title={`${riskItemsBadge} open risk item(s)`}
              style={{ marginLeft: 6, background: '#E0A800', color: '#fff', borderRadius: 10, padding: '0 7px', fontSize: 11, fontWeight: 700 }}>{riskItemsBadge}</span>
          )}
        </button>
      ))}
      {user?.role === 'admin' && (
        <button onClick={() => navigate('/admin/users')}
          style={{ ...btn(pathname === '/admin/users', '#E22D2D'), color: '#E22D2D', fontWeight: 600, background: pathname === '/admin/users' ? '#FCEBEB' : 'transparent' }}>
          🛡 Admin
        </button>
      )}
      {!allow && <GlobalSearch />}
    </nav>
  );
}
